import { Injectable } from '@angular/core';
import { ActivatedRouteSnapshot, RouterStateSnapshot, UrlTree, Router } from '@angular/router';
import { GetRoleQuery } from 'app/API.service';
import { UserService } from 'app/shared/user.service';
import { UsersSettingConstant } from 'app/users-settings/users-settings.constant';
import { Observable } from 'rxjs';
import { AppLevelRoleEnum } from '../enums/appLevelRoles.enum';
import { AppLevelRoleEnumThirdParty } from '../enums/appLevelRoles.enum';
import { UtilsService } from '../utils.service';
import { EntityService } from '../entity.service';
import { RootEntityContextService } from '../root-entity-context.service';

@Injectable({
  providedIn: 'root',
})
export class RoleGuard {
  mainRoute: string = null;
  subRoute: string = null;
  entityId: string = null;
  constructor(
    private userService: UserService,
    private router: Router,
    private entityService: EntityService,
    private rootEntityContextService: RootEntityContextService
  ) { }
  canActivate(
    route: ActivatedRouteSnapshot,
    state: RouterStateSnapshot
  ): Observable<boolean | UrlTree> | Promise<boolean | UrlTree> | boolean | UrlTree {
    return this.verifyRoute(state.url);
  }
  canActivateChild(
    childRoute: ActivatedRouteSnapshot,
    state: RouterStateSnapshot
  ): Observable<boolean | UrlTree> | Promise<boolean | UrlTree> | boolean | UrlTree {
    return this.verifyRoute(state.url);
  }

  CheckRouteEnableInEntity(entity) {
    const role = this.userService.currentUserRole;

    if (this.mainRoute === 'first-party') {
      // for policies route, check if policy management is enabled
      if (this.subRoute === 'policies') {
        return this.isChildRouteEnabled(entity, 'firstParty', this.subRoute);
      }

      // for regulatory-watch route, check if regulatory watch is enabled
      if (this.subRoute === 'regulatory-watch') {
        return this.isChildRouteEnabled(entity, 'firstParty', this.subRoute);
      }

      // for other first-party routes, check if firstParty is enabled
      if (entity?.solutionStatus?.firstParty) {
        return true;
      }
      return this.isChildRouteEnabled(entity, 'firstParty', this.subRoute);
    }

    if (this.mainRoute === 'third-party') {
      if (entity?.solutionStatus?.thirdParty) {
        return true;
      }
      return this.isChildRouteEnabled(entity, 'thirdParty', this.subRoute);
    }

    if (this.mainRoute === 'board') {
      return !!entity?.solutionStatus?.boardView;
    }

    return true;
  }

  private isChildRouteEnabled(entity: any, parentKey: 'firstParty' | 'thirdParty', subRoute: string): boolean {
    if (!entity?.solutionStatus?.items || !Array.isArray(entity.solutionStatus.items)) {
      return true;
    }

    const routeToChildKeyMap: Record<string, Record<string, string>> = {
      'first-party': {
        policies: 'policyManagement',
        'regulatory-watch': 'regulatoryWatch',
      },
      'third-party': {},
    };

    const childKey = routeToChildKeyMap[this.mainRoute]?.[subRoute];
    if (!childKey) {
      return false;
    }

    const enabledChild = entity.solutionStatus.items.find(
      (item: any) => item?.parentKey === parentKey && item?.key === childKey && item?.enabled === true
    );

    return !!enabledChild;
  }

  async verifyRoute(url: string = '') {
    try {
      [this.mainRoute, this.entityId, this.subRoute] = this.returnRoutes(url);
      await this.userService.getCurrentUser();
      const role = this.userService.currentUserRole;

      if (this?.entityId) {
        const entity = await this.rootEntityContextService.getRootEntityById(this.entityId);
        const isAllowed = [AppLevelRoleEnum.VENDOR, AppLevelRoleEnum.PARTICIPANT].includes(
          role?.name?.toLowerCase() as AppLevelRoleEnum
        )
          ? true
          : this.CheckRouteEnableInEntity(entity);

        if (!isAllowed) {
          if (role?.name?.toLowerCase() === AppLevelRoleEnum.POLICY_MANAGER.toLowerCase()) {
            const policyManagementEnabled = entity?.solutionStatus?.items?.find(
              item => item?.parentKey === 'firstParty' && item?.key === 'policyManagement' && item?.enabled === true
            );

            if (!policyManagementEnabled) {
              await this.router.navigate([`first-party/${this.entityId}/user-settings`]);
            } else {
              await this.router.navigate(['/not-authorized']);
            }
            return true;
          }

          if (role?.name?.toLowerCase() === AppLevelRoleEnum.REGULATION_MANAGER.toLowerCase()) {
            const regulatoryWatchEnabled = entity?.solutionStatus?.items?.find(
              item => item?.parentKey === 'firstParty' && item?.key === 'regulatoryWatch' && item?.enabled === true
            );

            if (!regulatoryWatchEnabled) {
              await this.router.navigate([`first-party/${this.entityId}/user-settings`]);
            } else {
              await this.router.navigate(['/not-authorized']);
            }
            return true;
          }

          if (entity?.solutionStatus?.firstParty) {
            await this.router.navigate([`first-party/${this.entityId}/upperdeck`]);
          } else {
            const childRoute = this.getFirstAllowedFirstPartyChild(entity);
            if (childRoute) {
              await this.router.navigate([`first-party/${this.entityId}/${childRoute}`]);
              return true;
            }
          }

          if (entity?.solutionStatus?.thirdParty) {
            await this.router.navigate([`third-party/${this.entityId}/overview`]);
          }
        }
      }
      if (role && role.screenPermissions) {
        const screenPermissions = JSON.parse(role.screenPermissions);
        if (role.name.toLowerCase() === AppLevelRoleEnum.ADMIN || role.name.toLowerCase() === AppLevelRoleEnum.MSSP) {
          screenPermissions['basic-routes'][2] = 'settings';
        }
        [this.mainRoute, this.entityId, this.subRoute] = this.returnRoutes(url);
        if (this.subRoute === 'default-settings') {
          this.subRoute = this.subRoute.split('-').join(' ');
        }
        this.checkForQueryParams();

        if (this.mainRoute && screenPermissions[this.mainRoute]) {
          if (screenPermissions[this.mainRoute].length && screenPermissions[this.mainRoute].includes(this.subRoute)) {
            return true;
          }
        }
        if (
          screenPermissions['basic-routes'] &&
          screenPermissions['basic-routes'].length &&
          screenPermissions['basic-routes'].includes(this.mainRoute)
        ) {
          return true;
        } else if (
          role &&
          role?.name?.toLowerCase() === AppLevelRoleEnum.VENDOR &&
          (screenPermissions.includes(this.mainRoute) || screenPermissions.includes(this.subRoute))
        ) {
          return true;
        }
        const newUrl = this.checkForFirstTimeLandingPages(url, role);
        // if newUrl has value, it means module is loading first time and it will land according to the permission.
        // uf newUrl is null, it will check for the third party case for collection and gaps.
        if (newUrl) {
          await this.router.navigate([newUrl]);
          return true;
        } else if (screenPermissions[this.mainRoute] && (this.subRoute === 'collection' || this.subRoute === 'gaps')) {
          return true;
        }
      } else if (!role) {
        await this.router.navigate(['/login']);
        return true;
      }
      this.userService.currentUserValue = null;
      await this.router.navigate(['/not-authorized']);
    } catch (e) {
      await this.router.navigate(['/login']);
      return Promise.reject(false);
    }
  }
  returnRoutes(url: string = ''): Array<string> {
    const routeArray: Array<string> = url.split('/');
    // routeArray have following this paths
    // routeArray[1] = contains the main route
    // routeArray[2] = contains entityId or subRoute (in case of bnb management)
    // routeArray[3] = contains the subMenu.
    if (
      UtilsService.isBnB &&
      routeArray &&
      routeArray.length &&
      routeArray[1].toLowerCase() === UsersSettingConstant.clients.toLowerCase()
    ) {
      return [routeArray[1], '', routeArray[2]];
    } else if (routeArray[1].toLowerCase() === UsersSettingConstant.clients.toLowerCase()) {
      return [routeArray[1], null, null];
    } else {
      return [routeArray[1], routeArray[2], routeArray[3]];
    }
  }

  checkForQueryParams() {
    const mainRouteQueryParams = this.mainRoute.split('?');
    const subRouteQueryParams = this.subRoute ? this.subRoute.split('?') : null;

    this.mainRoute = mainRouteQueryParams && mainRouteQueryParams.length ? mainRouteQueryParams[0] : this.mainRoute;
    this.subRoute = subRouteQueryParams && subRouteQueryParams.length ? subRouteQueryParams[0] : this.subRoute;
  }

  checkForFirstTimeLandingPages(url: string, role: GetRoleQuery): string {
    if (!this.subRoute && this.mainRoute === AppLevelRoleEnumThirdParty.ThirdPartyRole) {
      this.subRoute = 'overview';
    }

    const screenPermissions = role ? JSON.parse(role.screenPermissions) : [];
    if (
      screenPermissions &&
      screenPermissions[AppLevelRoleEnumThirdParty.ThirdPartyRole] &&
      screenPermissions[AppLevelRoleEnumThirdParty.ThirdPartyRole].length &&
      this.mainRoute === AppLevelRoleEnumThirdParty.ThirdPartyRole &&
      this.subRoute === 'overview'
    ) {
      this.subRoute =
        screenPermissions[AppLevelRoleEnumThirdParty.ThirdPartyRole][0] === AppLevelRoleEnumThirdParty.CustomVendors
          ? screenPermissions[AppLevelRoleEnumThirdParty.ThirdPartyRole][1]
          : screenPermissions[AppLevelRoleEnumThirdParty.ThirdPartyRole][0];
      return `${this.mainRoute}/${this.entityId}/${this.subRoute}`;
    } else if (
      screenPermissions &&
      screenPermissions['first-party'] &&
      screenPermissions['first-party'].length &&
      this.mainRoute === 'first-party' &&
      this.subRoute === 'upperdeck'
    ) {
      this.subRoute = screenPermissions['first-party'][0];
      return `${this.mainRoute}/${this.entityId}/${this.subRoute}`;
    } else if (screenPermissions && screenPermissions.board && screenPermissions.board.length && !this.subRoute) {
      this.subRoute = screenPermissions.board[0];
      return `${this.mainRoute}/${this.entityId}/${this.subRoute}`;
    } else {
      // If first-party is disabled but a child is enabled, land on that child
      if (this.mainRoute === 'first-party' && this.entityId) {
        const child = this.getFirstAllowedFirstPartyChildFromPermissions(role);
        if (child) {
          return `${this.mainRoute}/${this.entityId}/${child}`;
        }
      }
      return null;
    }
  }

  private getFirstAllowedFirstPartyChild(entity: any): string | null {
    const items = entity?.solutionStatus?.items;

    // If items array doesn't exist, return null (no default route)
    if (!Array.isArray(items)) {
      return null;
    }

    // Define the order of child routes to check
    const childOrder = ['policyManagement', 'regulatoryWatch'];
    const mapChildToRoute: Record<string, string> = {
      policyManagement: 'policies',
      regulatoryWatch: 'regulatory-watch',
    };

    // Check each child in order
    for (const child of childOrder) {
      const found = items.find(
        (item: any) => item?.parentKey === 'firstParty' && item?.key === child && item?.enabled === true
      );

      if (found) {
        return mapChildToRoute[child];
      }
    }

    return null;
  }

  private getFirstAllowedFirstPartyChildFromPermissions(role: GetRoleQuery): string | null {
    if (!role?.screenPermissions) {
      return null;
    }

    const perms = JSON.parse(role.screenPermissions);
    const childCandidates: Array<{ route: string; permission: string }> = [
      { route: 'policies', permission: 'policies' },
      { route: 'regulatory-watch', permission: 'regulatory-watch' },
    ];

    // Check each child candidate
    for (const candidate of childCandidates) {
      if (perms?.['first-party']?.includes(candidate.permission)) {
        return candidate.route;
      }
    }

    return null;
  }
}
